import { useState, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import {
  useGetEncounterTypesQuery,
  useCreateEncounterTypeMutation,
  useUpdateEncounterTypeMutation,
  useDeleteEncounterTypeMutation,
} from './api';
import {
  AppointmentType,
  CreateAppointmentTypeRequest,
  UpdateAppointmentTypeRequest,
} from '../types';
import { makeSelectFieldExpanded } from './selectors';
import { toggleExpandableField, setExpandableFieldState } from './slice';

/**
 * Hook for managing appointment type selection and search
 */
export function useAppointmentTypeSelection() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const selectAppointmentType = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedId(null);
  }, []);

  const updateSearchTerm = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return {
    selectedId,
    searchTerm,
    selectAppointmentType,
    clearSelection,
    updateSearchTerm,
  };
}

/**
 * Hook for fetching and filtering appointment types
 */
export function useAppointmentTypes(searchTerm?: string, providerId?: string) {
  // Fetch all appointment types (optionally for a specific provider)
  const {
    data: appointmentTypesResponse,
    isLoading,
    error,
    refetch,
  } = useGetEncounterTypesQuery({ perPage: 100, providerId });

  // Client-side filtering (until server-side search is implemented)
  const filteredAppointmentTypes = useMemo(() => {
    if (!appointmentTypesResponse?.items) return [];

    if (!searchTerm || searchTerm.trim() === '') {
      return appointmentTypesResponse.items;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return appointmentTypesResponse.items.filter(appointmentType =>
      appointmentType.encounter_type.toLowerCase().includes(lowerSearchTerm),
    );
  }, [appointmentTypesResponse?.items, searchTerm]);

  return {
    appointmentTypes: filteredAppointmentTypes,
    totalCount: appointmentTypesResponse?.total || 0,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for creating new appointment types
 */
export function useCreateAppointmentType() {
  const [createEncounterType, { isLoading: isCreating, error: createError }] =
    useCreateEncounterTypeMutation();

  const createNewAppointmentType = useCallback(
    async (appointmentTypeData: CreateAppointmentTypeRequest): Promise<AppointmentType | null> => {
      try {
        const result = await createEncounterType({
          encounterTypeCreate: appointmentTypeData,
        }).unwrap();
        return result;
      } catch (error) {
        console.error('Failed to create appointment type:', error);
        return null;
      }
    },
    [createEncounterType],
  );

  return {
    createNewAppointmentType,
    isCreating,
    createError,
  };
}

/**
 * Hook for updating appointment types
 */
export function useUpdateAppointmentType() {
  const [updateEncounterType, { isLoading: isUpdating, error: updateError }] =
    useUpdateEncounterTypeMutation();

  const updateAppointmentType = useCallback(
    async (
      encounterTypeId: string,
      updateData: UpdateAppointmentTypeRequest,
    ): Promise<AppointmentType | null> => {
      try {
        const result = await updateEncounterType({
          encounterTypeId,
          encounterTypeUpdate: updateData,
        }).unwrap();
        return result;
      } catch (error) {
        console.error('Failed to update appointment type:', error);
        return null;
      }
    },
    [updateEncounterType],
  );

  return {
    updateAppointmentType,
    isUpdating,
    updateError,
  };
}

/**
 * Hook for deleting appointment types
 */
export function useDeleteAppointmentType() {
  const [deleteEncounterType, { isLoading: isDeleting, error: deleteError }] =
    useDeleteEncounterTypeMutation();

  const deleteAppointmentType = useCallback(
    async (encounterTypeId: string): Promise<boolean> => {
      try {
        await deleteEncounterType({ encounterTypeId }).unwrap();
        return true;
      } catch (error) {
        console.error('Failed to delete appointment type:', error);
        return false;
      }
    },
    [deleteEncounterType],
  );

  return {
    deleteAppointmentType,
    isDeleting,
    deleteError,
  };
}

/**
 * Hook for managing expandable field state
 */
export function useExpandableField(fieldId: string) {
  const dispatch = useAppDispatch();
  const expanded = useAppSelector(makeSelectFieldExpanded(fieldId));

  const toggle = useCallback(() => {
    dispatch(toggleExpandableField(fieldId));
  }, [dispatch, fieldId]);

  const setExpanded = useCallback(
    (expanded: boolean) => {
      dispatch(setExpandableFieldState({ fieldId, expanded }));
    },
    [dispatch, fieldId],
  );

  return {
    expanded,
    toggle,
    setExpanded,
  };
}

/**
 * Main hook that combines all appointment type functionality
 */
export function useAppointmentTypeManagement() {
  const selection = useAppointmentTypeSelection();
  const appointmentTypesQuery = useAppointmentTypes(selection.searchTerm);
  const creation = useCreateAppointmentType();

  return {
    // Selection state
    ...selection,

    // Data fetching
    ...appointmentTypesQuery,

    // Creation
    ...creation,
  };
}
